import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import {
  DetailsEsimDisplay,
  DetailsSection,
} from "@/components/Portal/DeviceManagement/Details/DetailsEsimDisplay/DetailsEsimDisplay";
import { DeviceNameRow } from "@/components/Portal/DeviceManagement/Details/DeviceNameRow/DeviceNameRow";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { SubscriptionActionButton } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionActionButton/SubscriptionActionButton";
import { PriceRow } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/SubscriptionPrice";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { getSim } from "@/services/api";
import { SimCardDetail } from "@/services/model";
import {
  isEditingSimDetailsAtom,
  isSimDetailsSectionActiveAtom,
} from "@/utils/atoms";
import {
  getSimTypeLabel,
  hasNumberPorting,
  iccidWithSpaces,
  isInactive,
  toPhoneNumber,
} from "@/utils/deviceUtils";
import { isFlatHomeTariff } from "@/utils/tariffUtils";
import { capitalizeFirstLetter } from "@/utils/translationHelpers";

import { NetworkReset } from "./NetworkReset/NetworkReset";
import { useSimMutation } from "./useSimMutation";

export type DeviceDetailsForm = { alias: string };

/**
 * Every section on this tab (device name, eSIM actions, network reset) opens
 * inline, one at a time. Figma: on tablet/desktop the other cards stay put
 * but grey out their own action; on mobile they disappear entirely so the
 * open section reads as its own screen (see `/user/security`'s LoginData for
 * the same pattern) - no route ever changes underneath either way.
 */
export const DetailsDisplay: FC = () => {
  const { t } = useTranslation("portal");
  const isMobile = useIsMobile();

  const [isEditingDeviceName, setIsEditingDeviceName] = useAtom(
    isEditingSimDetailsAtom,
  );
  const [activeSection, setActiveSection] = useState<DetailsSection | null>(
    null,
  );
  const [, setSimDetailsSectionActive] = useAtom(isSimDetailsSectionActiveAtom);
  const isAnySectionActive = isEditingDeviceName || activeSection !== null;

  useEffect(() => {
    setSimDetailsSectionActive(activeSection !== null);
  }, [activeSection, setSimDetailsSectionActive]);

  useEffect(
    () => () => {
      setSimDetailsSectionActive(false);
    },
    [setSimDetailsSectionActive],
  );

  const [simId] = useSimIdFromQueryParam();
  const [simDetail, setSimDetail] = useState<SimCardDetail | undefined>(
    undefined,
  );

  const { data: displayedSim } = useDisplayedSim();
  const isFlatHome = isFlatHomeTariff(displayedSim?.serviceID);
  // Figma: while a number is porting, the tab drops down to just Gerätedetails
  // + SIM Details - eSIM actions and network reset aren't offered mid-port.
  const isPorting = !!displayedSim && hasNumberPorting(displayedSim);
  const shouldHideEsimSection =
    isFlatHome ||
    isPorting ||
    !!displayedSim?.servicePending ||
    isInactive(displayedSim);
  const shouldHideTroubleshootingSection =
    isPorting || !!displayedSim?.servicePending || isInactive(displayedSim);

  const formMethods = useForm<DeviceDetailsForm>({
    defaultValues: async () => {
      const simData = await getSim({ simId: Number(simId) });
      setSimDetail(simData);

      return {
        alias: simData.alias ?? "",
      };
    },
    mode: "onChange",
  });
  const hasValidationErrors = !!Object.keys(formMethods.formState.errors)
    .length;

  const { mutate } = useSimMutation();
  const updateDeviceDetails = (data: DeviceDetailsForm) => {
    if (!data) {
      return;
    }
    if (simDetail?.simId) {
      mutate({
        params: { simId: simDetail.simId },
        data: { sim_alias: data.alias },
      });
    }
    setIsEditingDeviceName(false);
  };
  const cancelEditingDeviceName = () => {
    formMethods.reset();
    setIsEditingDeviceName(false);
  };

  // A section that isn't the active one greys out its own action while
  // another is open, and disappears entirely on mobile (Figma: the open
  // section takes over the whole screen there).
  const isSectionBlocked = (section: DetailsSection) =>
    isAnySectionActive && activeSection !== section;
  const isDeviceNameBlocked = isAnySectionActive && !isEditingDeviceName;

  return (
    <TwoColumnLayout
      rows={[
        {
          left: (
            <div className="flex w-full flex-col gap-gutter">
              <div hidden={isMobile && isDeviceNameBlocked}>
                <FormProvider {...formMethods}>
                  <form
                    onSubmit={formMethods.handleSubmit(updateDeviceDetails)}
                  >
                    <SubscriptionCard
                      dataTestid="device-settings"
                      stickyHeader={isEditingDeviceName}
                      title={t(
                        "cockpit.managementTile.details.sections.details.deviceDetails",
                      )}
                      action={
                        // Flat Home's device name is fixed for the whole
                        // contract term - same "nothing to change" case
                        // SubscriptionDisplay's canChangeOptions guards
                        // against, just for the device name.
                        !isFlatHome &&
                        (isEditingDeviceName ? (
                          <>
                            <Button
                              dataTestid="device-name-cancel"
                              size="sm"
                              variant="secondary"
                              onClick={cancelEditingDeviceName}
                            >
                              {capitalizeFirstLetter(
                                t("common.buttons.cancel"),
                              )}
                            </Button>
                            <Button
                              dataTestid="device-name-save"
                              size="sm"
                              variant="primary"
                              type="submit"
                              disabled={hasValidationErrors}
                            >
                              {capitalizeFirstLetter(t("common.buttons.save"))}
                            </Button>
                          </>
                        ) : (
                          <SubscriptionActionButton
                            dataTestid="device-name-edit"
                            label={capitalizeFirstLetter(
                              t("common.buttons.change"),
                            )}
                            disabledHint={
                              isDeviceNameBlocked
                                ? t(
                                    "cockpit.managementTile.details.sections.details.disabledTitle",
                                  )
                                : undefined
                            }
                            onClick={() => setIsEditingDeviceName(true)}
                          />
                        ))
                      }
                    >
                      <DeviceNameRow isEditing={isEditingDeviceName} />
                    </SubscriptionCard>
                  </form>
                </FormProvider>
              </div>

              <div
                hidden={
                  shouldHideEsimSection ||
                  (isMobile &&
                    isAnySectionActive &&
                    activeSection !== "reloadEsim" &&
                    activeSection !== "transferEsim" &&
                    activeSection !== "physicalToEsim")
                }
              >
                <DetailsEsimDisplay
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  isBlocked={
                    isEditingDeviceName ||
                    (activeSection !== null &&
                      activeSection !== "reloadEsim" &&
                      activeSection !== "transferEsim" &&
                      activeSection !== "physicalToEsim")
                  }
                />
              </div>

              <div
                hidden={
                  shouldHideTroubleshootingSection ||
                  (isMobile && isSectionBlocked("networkReset"))
                }
              >
                <NetworkReset
                  disabled={!simDetail?.networkResetAllowed}
                  reasons={simDetail?.networkResetNotAllowedReasons}
                  isActive={activeSection === "networkReset"}
                  isBlocked={
                    isEditingDeviceName ||
                    (activeSection !== null && activeSection !== "networkReset")
                  }
                  onOpen={() => setActiveSection("networkReset")}
                  onClose={() => setActiveSection(null)}
                />
              </div>

              <div hidden={isMobile && isAnySectionActive}>
                <SubscriptionCard
                  dataTestid="device-details"
                  title={t(
                    "cockpit.managementTile.details.sections.details.simDetails",
                  )}
                >
                  <PriceRow
                    withDivider
                    variant="strong"
                    label={t("common.device.attributes.simType")}
                    value={getSimTypeLabel(simDetail?.simType, t)}
                  />
                  <PriceRow
                    withDivider
                    variant="strong"
                    label={t("common.device.attributes.simNumber")}
                    value={iccidWithSpaces(simDetail?.iccid)}
                  />
                  <PriceRow
                    withDivider
                    variant="strong"
                    label={t("common.device.attributes.telephoneNumber")}
                    value={toPhoneNumber(simDetail?.msisdn || "")}
                  />
                  <PriceRow
                    withDivider
                    variant="strong"
                    label={t("common.device.attributes.pin")}
                    value={simDetail?.pin || "-"}
                  />
                  <PriceRow
                    variant="strong"
                    label={t("common.device.attributes.puk")}
                    value={simDetail?.puk || "-"}
                  />
                </SubscriptionCard>
              </div>
            </div>
          ),
        },
      ]}
    />
  );
};
